import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, Sparkles, X } from 'lucide-react';
import { useState } from 'react';

export interface LeadOption {
    id: string;
    lead_number: string | null;
    /** The customer, shown as a badge — leads are identified by number, not name. */
    company_name?: string | null;
    /** Optional free-text name the lead was given. */
    name?: string | null;
}

interface LeadSelectProps {
    /** The candidate list. The caller decides which leads are eligible. */
    leads: LeadOption[];
    value: string | null;
    onValueChange: (value: string | null) => void;
    disabled?: boolean;
    className?: string;
    labels?: {
        placeholder?: string;
        search?: string;
        empty?: string;
        clear?: string;
    };
}

function label(lead: LeadOption | null | undefined): string {
    if (!lead) {
        return '';
    }

    return [lead.lead_number, lead.name].filter(Boolean).join(' · ');
}

/**
 * Searchable lead picker, built to the same shape as PersonSelect: a controlled,
 * prop-fed popover over a Command list, matching its trigger's width.
 *
 * Prop-fed rather than self-fetching for the same reason — the caller decides which
 * leads are eligible (here: not yet converted, with an approved offer), and the
 * server validates the same set.
 */
export function LeadSelect({ leads, value, onValueChange, disabled = false, className, labels }: LeadSelectProps) {
    const { t } = useTranslations();
    const [open, setOpen] = useState(false);

    const placeholder = labels?.placeholder ?? t('projects.create.placeholders.lead', 'Select a lead…');
    const searchLabel = labels?.search ?? t('projects.create.placeholders.search_lead', 'Search leads…');
    const emptyLabel = labels?.empty ?? t('projects.create.placeholders.no_leads', 'No convertible leads found.');
    const clearLabel = labels?.clear ?? t('projects.create.clear_lead', 'No lead');

    const selected = value ? (leads.find((lead) => lead.id === value) ?? null) : null;

    return (
        <div className={cn('relative flex w-full items-center', className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            'h-auto w-full justify-start gap-2 py-1.5 pl-1.5 font-normal',
                            // Leave room for the clear control so the label never runs under it.
                            selected && !disabled && 'pr-9',
                            !selected && 'text-muted-foreground',
                        )}
                    >
                        <span
                            className={cn(
                                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',
                                selected ? 'bg-primary/10 text-primary border-transparent' : 'border-muted-foreground/40 border-dashed',
                            )}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                        </span>
                        <span className="truncate text-sm">{selected ? label(selected) : placeholder}</span>
                        {selected?.company_name ? (
                            <Badge variant="secondary" className="ml-auto shrink-0 text-xs">
                                {selected.company_name}
                            </Badge>
                        ) : null}
                    </Button>
                </PopoverTrigger>

                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                        <CommandInput placeholder={searchLabel} />
                        <CommandList>
                            <CommandEmpty>{emptyLabel}</CommandEmpty>
                            <CommandGroup>
                                {leads.map((lead) => (
                                    <CommandItem
                                        key={lead.id}
                                        // Searchable by number, name and customer alike.
                                        value={`${lead.lead_number ?? ''} ${lead.name ?? ''} ${lead.company_name ?? ''} ${lead.id}`}
                                        onSelect={() => {
                                            onValueChange(lead.id === value ? null : lead.id);
                                            setOpen(false);
                                        }}
                                        className="gap-2"
                                    >
                                        <span className="min-w-0 flex-1 truncate text-sm">{label(lead)}</span>
                                        {lead.company_name ? (
                                            <Badge variant="secondary" className="shrink-0 text-xs">
                                                {lead.company_name}
                                            </Badge>
                                        ) : null}
                                        {lead.id === value ? <Check className="text-primary h-4 w-4 shrink-0" /> : null}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {selected && !disabled ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    // stopPropagation so clearing does not also open the popover.
                    onClick={(event) => {
                        event.stopPropagation();
                        onValueChange(null);
                    }}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-6 w-6 -translate-y-1/2"
                    aria-label={clearLabel}
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            ) : null}
        </div>
    );
}
